import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import type { LastActionUserDto } from 'src/app/proxy/service-orders';
import { ActionResourceServiceOrder } from 'src/app/proxy/service-orders';
import { Resource } from 'src/app/shared/local-db/models/resource';
import { ServiceOrderItem } from 'src/app/shared/local-db/models/service-order';

@Injectable()
export class ResourceEditService {
  private resourcesItems: Resource[] = [];
  private temporalResourceToDelete = new Set<number>();
  private readonly _temporalResourceToDelete$: BehaviorSubject<number[]> = new BehaviorSubject<
    number[]
  >([]);
  temporalResourceToDeleteChanged$: Observable<number[]> =
    this._temporalResourceToDelete$.asObservable();
  private readonly _selectedResourceItems$: BehaviorSubject<Resource[]> = new BehaviorSubject<
    Resource[]
  >([]);
  selectedResourcesItemsChanged$: Observable<Resource[]> =
    this._selectedResourceItems$.asObservable();
  private serviceOrderToEdit: ServiceOrderItem[] = [];
  private readonly _serviceOrderToEdit$: BehaviorSubject<ServiceOrderItem[]> = new BehaviorSubject<
    ServiceOrderItem[]
  >([]);
  _serviceOrderToEditChanged$: Observable<ServiceOrderItem[]> =
    this._serviceOrderToEdit$.asObservable();
  private currentLastActionUser: LastActionUserDto | null = null;

  setCurrentLastActionUser(lastActionUser: LastActionUserDto | null) {
    this.currentLastActionUser = lastActionUser;
  }

  setResources(resources: Resource[]) {
    this.resourcesItems = resources;
    this._selectedResourceItems$.next(this.resourcesItems);
  }

  setServiceOrdersFiltersToEdit(serviceOrderToEdit: ServiceOrderItem[]) {
    this.serviceOrderToEdit = serviceOrderToEdit;
    this._serviceOrderToEdit$.next(this.serviceOrderToEdit);
  }

  addNewResource(resource: Resource, serviceOrdersToAdd: ServiceOrderItem[], performedByUserId:string) {
    this.addNewServiceOrders(serviceOrdersToAdd, performedByUserId);
    this.syncResourceState(resource.resourceTypeId, resource);
    this._selectedResourceItems$.next(this.resourcesItems);
    this._serviceOrderToEdit$.next(this.serviceOrderToEdit);
  }

  addNewServiceOrders(toAdd:ServiceOrderItem[], performedByUserId:string){
    toAdd.forEach(soToAdd=>{
      let so = this.serviceOrderToEdit.find(x=>x.idComposed == soToAdd.idComposed);
      if(so){
        this.restoreOrUpdateExistingServiceOrder(so, soToAdd, performedByUserId);
      }
      else{
        ServiceOrderItem.applyLastActionUser(soToAdd, this.currentLastActionUser);
        this.serviceOrderToEdit.push(soToAdd);
      }
    });
  }

  modifyResource(
    resource: Resource,
    serviceOrdersToModify: ServiceOrderItem[],
    isDeleted: boolean,
    performedByUserId:string
  ) {
    let idsToModify = new Set(serviceOrdersToModify.map(x => x.idComposed));
    let serviceOrderToModifyDict = serviceOrdersToModify.reduce((acc, item) => {
      if (!acc[item.idComposed]) {
        acc[item.idComposed] = item;
      }
      return acc;
    }, {} as Map<number, (typeof serviceOrdersToModify)[0]>);

    this.serviceOrderToEdit = this.serviceOrderToEdit.map(x => {
      if (idsToModify.has(x.idComposed)) {
        const nextServiceOrder = serviceOrderToModifyDict[x.idComposed];
        this.applyServiceOrderStateChange(
          x,
          nextServiceOrder.serviceOrderRecommendedQuantity,
          isDeleted,
          performedByUserId
        );
      }
      return x;
    });

    this.syncResourceState(resource.resourceTypeId, resource);
  }

  addToDelete(resourceTypeId: number) {
    if (!this.temporalResourceToDelete.has(resourceTypeId)) {
      this.temporalResourceToDelete.add(resourceTypeId);
      this._temporalResourceToDelete$.next(Array.from(this.temporalResourceToDelete));
    }
  }

  removeToDelete(resourceTypeId: number) {
    if (this.temporalResourceToDelete.has(resourceTypeId)) {
      this.temporalResourceToDelete.delete(resourceTypeId);
      this._temporalResourceToDelete$.next(Array.from(this.temporalResourceToDelete));
    }
  }

  confirmDeleteMultiple(performedByUserId:string) {
    if (this.temporalResourceToDelete.size > 0) {
      let resourceToDelete = new Set(
        this.resourcesItems
          .filter(x => this.temporalResourceToDelete.has(x.resourceTypeId))
          .map(x => x.resourceTypeId)
      );

      let soByResourceToDelete = new Set(
        this.serviceOrderToEdit
          .filter(x => this.temporalResourceToDelete.has(x.resourceTypeId))
          .map(x => x.idComposed)
      );

      this.resourcesItems = this.resourcesItems.map(x => {
        if (resourceToDelete.has(x.resourceTypeId)) {
          x.isMarkedForDeletion = true;
          x.isModified = false;
        }
        return x;
      });
      
      this.serviceOrderToEdit = this.serviceOrderToEdit.map(x => {
        if (soByResourceToDelete.has(x.idComposed)) {
          this.applyServiceOrderStateChange(
            x,
            x.serviceOrderRecommendedQuantity,
            true,
            performedByUserId
          );
        }
        return x;
      });

      this.serviceOrderToEdit = this.serviceOrderToEdit.filter(x=>x != null);

      this._selectedResourceItems$.next(this.resourcesItems);
      this._serviceOrderToEdit$.next(this.serviceOrderToEdit);
    }
  }

  cleanState(){
    this.resourcesItems = [];
    this.temporalResourceToDelete = new Set<number>();
    this.serviceOrderToEdit = [];
  }

  private restoreOrUpdateExistingServiceOrder(
    currentServiceOrder: ServiceOrderItem,
    nextServiceOrder: ServiceOrderItem,
    performedByUserId: string
  ) {
    this.applyServiceOrderStateChange(
      currentServiceOrder,
      nextServiceOrder.serviceOrderRecommendedQuantity,
      false,
      performedByUserId
    );
    currentServiceOrder.serviceOrderPlannedQuantity = nextServiceOrder.serviceOrderPlannedQuantity;
    currentServiceOrder.resourceDeleted = false;
  }

  private applyServiceOrderStateChange(
    serviceOrder: ServiceOrderItem,
    recommendedQuantity: number | undefined,
    isDeleted: boolean,
    performedByUserId: string
  ) {
    serviceOrder.serviceOrderRecommendedQuantity = recommendedQuantity;
    serviceOrder.performedByUserId = performedByUserId;
    serviceOrder.resourceDeleted = false;

    if (isDeleted) {
      serviceOrder.isModified = false;
      serviceOrder.isMarkedForDeletion = true;
      serviceOrder.isPending = false;
      serviceOrder.actualActionResource = serviceOrder.isNew
        ? ActionResourceServiceOrder.Added
        : ActionResourceServiceOrder.Deleted;
      ServiceOrderItem.applyLastActionUser(serviceOrder, this.currentLastActionUser);
      return;
    }

    serviceOrder.isMarkedForDeletion = false;
    serviceOrder.isPending = false;

    if (serviceOrder.isNew) {
      serviceOrder.isModified = true;
      serviceOrder.actualActionResource = ActionResourceServiceOrder.Added;
      ServiceOrderItem.applyLastActionUser(serviceOrder, this.currentLastActionUser);
      return;
    }

    serviceOrder.isModified = true;
    serviceOrder.actualActionResource = ActionResourceServiceOrder.Modified;
    ServiceOrderItem.applyLastActionUser(serviceOrder, this.currentLastActionUser);
  }

  private syncResourceState(resourceTypeId: number, resourceFallback: Resource) {
    const serviceOrdersByResource = this.serviceOrderToEdit.filter(
      x => x.resourceTypeId == resourceTypeId
    );

    if (!serviceOrdersByResource.length) {
      return;
    }

    const visibleServiceOrders = serviceOrdersByResource.filter(x => !x.isMarkedForDeletion && !x.resourceDeleted);
    const referenceServiceOrder = visibleServiceOrders[0] ?? serviceOrdersByResource[0];

    const shouldHideResource = visibleServiceOrders.length === 0;
    const hasModifiedItems = serviceOrdersByResource.some(x => !!x.isModified);
    const hasDeletedItems = serviceOrdersByResource.some(x => !!x.isMarkedForDeletion);

    const existingResourceIndex = this.resourcesItems.findIndex(
      x => x.resourceTypeId == resourceTypeId
    );

    const nextResource: Resource = {
      ...(existingResourceIndex >= 0 ? this.resourcesItems[existingResourceIndex] : resourceFallback),
      code: referenceServiceOrder.resourceCode ?? resourceFallback.code,
      resourceId: referenceServiceOrder.resourceId ?? resourceFallback.resourceId,
      resourceTypeId,
      resourceName: referenceServiceOrder.resourceName ?? resourceFallback.resourceName,
      resourceTypeName: referenceServiceOrder.resourceName ?? resourceFallback.resourceTypeName,
      serviceOrderPlannedQuantity:
        referenceServiceOrder.serviceOrderPlannedQuantity ?? resourceFallback.serviceOrderPlannedQuantity,
      serviceOrderRecommendedQuantity:
        referenceServiceOrder.serviceOrderRecommendedQuantity ?? resourceFallback.serviceOrderRecommendedQuantity,
      resourceUnitOfMeasure:
        referenceServiceOrder.resourceUnitOfMeasure ?? resourceFallback.resourceUnitOfMeasure,
      resourceDeleted: shouldHideResource,
      isMarkedForDeletion: shouldHideResource && hasDeletedItems,
      isModified: !shouldHideResource && hasModifiedItems,
    };

    this.upsertResource(resourceTypeId, nextResource);
  }

  private upsertResource(resourceTypeId: number, resource: Resource) {
    const resourceIndex = this.resourcesItems.findIndex(x => x.resourceTypeId == resourceTypeId);

    if (resourceIndex >= 0) {
      this.resourcesItems[resourceIndex] = {
        ...this.resourcesItems[resourceIndex],
        ...resource,
      };
      return;
    }

    this.resourcesItems.push(resource);
  }
}
